import React, {
  forwardRef,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import PaginationBar from 'components/v2/PaginationBar';
import { ColumnCategoriesDropdown } from './ColumnCategoriesDropdown';
import EditableColumn from './customCellRenderers/EditableColumn';
import { getStopPrefixAndField } from './columns';
import { getInitialDateRange, loadColumnState } from './transformers';

const gridOpts = {
  defaultColDef: {
    enableRowGroup: true,
    enablePivot: false,
    sortable: true,
    resizable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    cellRenderer: EditableColumn,
  },
  rowDragManaged: true,
  copyHeadersToClipboard: true,
  suppressColumnVirtualisation: true,
};

const ShipmentExplorer = forwardRef(function ShipmentExplorer(
  {
    items,
    getRowId,
    components,
    statusBar,
    columnDefs,
    onSortChanged,
    onSelectionChanged,
    onRowDisplayed,
    stopGroupStats,
    onFilterChange,
    onEditingStopped,
    localeText,
    loadingText,
    sidebarParams,
    paginationTotal,
    paginationPage,
    paginationPerPage,
    paginationPerPageOptions,
    report,
    onPageClick,
    onPerPageChange,
    handlePagination,
    unifiedViewV2Enabled,
  },
  ref,
) {
  const gridRef = useRef(null);
  const apiRef = useRef(null);
  const columnApiRef = useRef(null);
  const initialFilterModelRef = useRef(report?.model);

  useEffect(() => {
    if (report?.model) {
      initialFilterModelRef.current = report.model;
    }
  }, [report]);

  useImperativeHandle(ref, () => ({
    get api() {
      return apiRef.current;
    },
    get columnApi() {
      return columnApiRef.current;
    },
    getSelectedRows: () => {
      if (!apiRef.current) {
        return [];
      }
      return apiRef.current.getSelectedRows().map((s) => ({
        ...s,
        _id: s.shipment_oid,
        id: s.shipment_oid,
      }));
    },
    resetView: () => {
      applyReport();
    },
  }));

  const applyReport = () => {
    if (report) {
      loadColumnState(report, apiRef.current, [], unifiedViewV2Enabled);

      if (report.rowGroupColumns?.length > 0) {
        apiRef.current.setRowGroupColumns(report.rowGroupColumns);
      }
    }
  };

  const handleColumnVisible = useCallback(
    (event) => {
      const { visible, column } = event;

      const colId = column?.colId;

      if (!colId) {
        return;
      }

      const res = getStopPrefixAndField(colId);
      if (!res.isStopGroupField) {
        return;
      }
      const count = stopGroupStats[res.prefix];

      if (count === 1) {
        return;
      }

      const { prefix, field } = res;

      const otherColumns = Array.from(Array(count).keys())
        .filter((index) => {
          const stopGroupField = `${prefix}__${index}__${field}`;
          if (colId === stopGroupField) {
            return false;
          }

          const col = columnApiRef.current?.getColumn(stopGroupField);
          if (!col || col.visible === visible) {
            return false;
          }
          return true;
        })
        .map((index) => {
          const colId = `${prefix}__${index}__${field}`;

          return {
            colId,
            hide: !visible,
          };
        });

      if (otherColumns.length === 0) {
        return;
      }

      apiRef.current?.applyColumnState({
        state: otherColumns,
        applyOrder: true,
      });
    },
    [stopGroupStats],
  );

  const onFilterChanged = useCallback(() => {
    onFilterChange(apiRef.current.getFilterModel() || {});
  }, []);

  const setModelToInitial = (api) => {
    // Apply initial filter model (from saved report or default created_at)
    const filterModel =
      initialFilterModelRef.current &&
      Object.keys(initialFilterModelRef.current).length > 0
        ? initialFilterModelRef.current
        : {
            created_at: {
              ...getInitialDateRange(),
              filterType: 'date',
              type: 'inRange',
            },
          };
    api.setFilterModel(filterModel);
    return filterModel;
  };

  const onGridReady = useCallback(
    (params) => {
      const { api, columnApi } = params;

      // Store api references
      apiRef.current = api;
      columnApiRef.current = columnApi;

      // Initialize grid
      api.showLoadingOverlay();
      api.autoSizeColumns();

      // Set up event listeners
      api.addEventListener('modelUpdated', () => {
        if (onRowDisplayed) {
          onRowDisplayed();
        }
      });

      api.addEventListener('columnVisible', handleColumnVisible);

      if (onEditingStopped) {
        api.addEventListener('cellEditingStopped', onEditingStopped);
      }

      const filterModel = setModelToInitial(api);

      if (!report) {
        onFilterChange(filterModel);
      } else {
        handlePagination();
        applyReport();
      }
    },
    [onRowDisplayed, handleColumnVisible, onFilterChange, onEditingStopped],
  );

  return (
    <div data-testid="div-da5f52c8" className="h-full w-full">
      <PaginationBar
        data-testid="pagination-bar-uv-shipments"
        total={paginationTotal}
        page={paginationPage}
        perPage={paginationPerPage}
        perPageOptions={paginationPerPageOptions}
        onPageClick={onPageClick}
        onPerPageChange={onPerPageChange}
      >
        <ColumnCategoriesDropdown api={apiRef.current} />
      </PaginationBar>
      <div className="px-8 pt-4 h-full">
        <AgGridReact
          ref={gridRef}
          rowData={items}
          maintainColumnOrder
          rememberGroupStateWhenNewData
          animateRows
          getRowId={getRowId}
          suppressClickEdit
          suppressRowClickSelection
          enableCellTextSelection
          enableRangeSelection
          rowSelection="multiple"
          components={components}
          multiSortKey="ctrl"
          onFilterChanged={onFilterChanged}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
          statusBar={statusBar}
          columnDefs={columnDefs}
          defaultColDef={gridOpts.defaultColDef}
          onSelectionChanged={onSelectionChanged}
          localeText={localeText}
          overlayLoadingTemplate={`<div><span class="text-base text-green font-bold">${loadingText}</span><img src="https://3qjglz4aka5y3jyx13q7je0p-wpengine.netdna-ssl.com/wp-content/uploads/2021/01/logo-header.svg" alt="" class="img-fluid"></div>`}
          sideBar={sidebarParams}
          allowDragFromColumnsToolPanel
        />
      </div>
    </div>
  );
});

ShipmentExplorer.propTypes = {
  items: PropTypes.array,
  getRowId: PropTypes.func.isRequired,
  components: PropTypes.object,
  statusBar: PropTypes.object,
  columnDefs: PropTypes.array,
  onSortChanged: PropTypes.func.isRequired,
  onSelectionChanged: PropTypes.func.isRequired,
  onRowDisplayed: PropTypes.func,
  stopGroupStats: PropTypes.object,
  onFilterChange: PropTypes.func,
  onEditingStopped: PropTypes.func,
  localeText: PropTypes.object,
  loadingText: PropTypes.string,
  sidebarParams: PropTypes.object,
  paginationTotal: PropTypes.number.isRequired,
  paginationPage: PropTypes.number.isRequired,
  paginationPerPage: PropTypes.number.isRequired,
  paginationPerPageOptions: PropTypes.array.isRequired,
  report: PropTypes.object,
  onPageClick: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  handlePagination: PropTypes.func.isRequired,
  unifiedViewV2Enabled: PropTypes.bool,
};

ShipmentExplorer.defaultProps = {
  items: [],
  components: {},
  statusBar: {},
  columnDefs: [],
  onRowDisplayed: undefined,
  stopGroupStats: {},
  onFilterChange: undefined,
  onEditingStopped: undefined,
  localeText: {},
  loadingText: 'Loading...',
};

export default ShipmentExplorer;
